import { PATIENT_CSRF_TOKEN } from "constants/StorageConstants"
import { DateTime } from "luxon"
import { axiosPatient } from "services/generic/AxiosInterceptor/AxiosInterceptor"

import { PatientAccountDataResponse } from "@shared/dtos/responses/patientAccountResponses.dto"
import { createSingletonGetter } from "@shared/utils/createSingletonGetter"

import { CustomStorage } from "./CustomStorage"

/**
 *  The state of the currently logged in patient.
 */
export interface PatientState {
  account: PatientAccountDataResponse
  expires: DateTime
}

/**
 *  A listener that will be notified when the patient state changes.
 */
export interface CurrentPatientStorageListener {
  (state: PatientState | undefined): void
}

/**
 *  This is a special class that stores information about the currently logged in patient.
 *  For historical reasons, it also uses local storage to store the CSRF token.
 */
export class CurrentPatientStorage extends CustomStorage<PatientState | null | undefined> {
  /**
   *  A constructor to set the initial state. The initial state can be set to
   *  undefined for now.
   */
  constructor() {
    super(undefined)

    // We need to check if the CSRF token is present in the local storage. If we call the API without
    // we will end up in an infinite loop cause the missing CSRF token will cause the API to complain
    // and reload the page which in turn will prompt this constructor to run again and again and again...
    //
    // This should be safe even given the fact that we have an ability to disable CSRF protection. This
    // loophole used to be used only for postman tests which we aren't doing any more.
    if (localStorage.getItem(PATIENT_CSRF_TOKEN)) {
      void axiosPatient
        .get("/patient/patientaccount/current")
        .then((response) => {
          super.store({
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            account: response.data.patient,

            // out setup has a limitation on how we handle session expiration. It's explained
            // in the PatientAccountPatientController.currentAccount method.
            expires: DateTime.now().plus({ minutes: 30 }),
          })
        })
        .catch(() => {
          localStorage.removeItem(PATIENT_CSRF_TOKEN)
        })
    }
  }

  /**
   * Store the patient state in the storage.
   */
  store(patient: PatientAccountDataResponse, expires: DateTime, csrfToken: string | undefined): void
  store(state: PatientState | undefined): void
  store(
    stateOrPatient: PatientState | undefined | PatientAccountDataResponse,
    expires?: DateTime,
    csrfToken?: string | undefined
  ) {
    // are we dealing with a reset?
    if (stateOrPatient === undefined) {
      super.store(undefined)
      return
    }

    // are we dealing with a response data?
    if (
      "fullName" in stateOrPatient &&
      "dateOfBirth" in stateOrPatient &&
      "gender" in stateOrPatient &&
      "practice" in stateOrPatient &&
      expires
    ) {
      const patient = stateOrPatient as PatientAccountDataResponse
      const state: PatientState = {
        account: patient,
        expires: expires,
      }
      // for historical reasons, we also store the CSRF token in the local storage
      if (csrfToken) {
        localStorage.setItem(PATIENT_CSRF_TOKEN, csrfToken)
      }
      super.store(state)
    }

    // we seems to be dealing with a state
    else {
      const state = stateOrPatient as PatientState
      super.store(state)
    }
  }

  /**
   * Clear the patient state from the storage.
   */
  clear() {
    super.store(undefined)

    // also clear the CSRF token as the storage sets it when the patient state is set.
    localStorage.removeItem(PATIENT_CSRF_TOKEN)
  }
}

/**
 *  Get the current instance of the current patient storage. The function
 *  returns a stable reference (it's always the same reference) of a CurrentPatientStorage
 *  instance that can be used throughout the application.
 */
export const getCurrentPatientStorage = createSingletonGetter(CurrentPatientStorage)
